// import { theme } from 'antd';
import { createContext, useContext, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/hooks/reduxHooks';
import { setTheme as setAppTheme } from '@/store/slices/themeSlice';
import { ThemeType } from '@/interfaces/interfaces';

type Theme = 'dark' | 'light' | 'system';

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
};

const initialState: ThemeProviderState = {
  theme: 'system',
  setTheme: () => null
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export default function ThemeProvider({
  children,
  defaultTheme = 'light',
  storageKey = 'vite-ui-theme',
  ...props
}: ThemeProviderProps) {
  const dispatch = useAppDispatch();
  const appTheme = useAppSelector((state) => state.theme.theme);
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || appTheme || defaultTheme
  );

  useEffect(() => {
    const root = window.document.documentElement;
    const resolvedTheme =
      theme === 'system'
        ? window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light'
        : theme;

    root.classList.remove('light', 'dark');
    root.classList.add(resolvedTheme);
    root.setAttribute('data-theme', resolvedTheme);
    root.style.colorScheme = resolvedTheme;
  }, [theme]);

  useEffect(() => {
    if (theme !== 'system' && theme !== appTheme) {
      setTheme(appTheme);
    }
  }, [appTheme, theme]);

  const value = {
    theme,
    setTheme: (nextTheme: Theme) => {
      const resolvedTheme: ThemeType =
        nextTheme === 'system'
          ? window.matchMedia('(prefers-color-scheme: dark)').matches
            ? 'dark'
            : 'light'
          : nextTheme;

      localStorage.setItem(storageKey, nextTheme);
      localStorage.setItem('theme', resolvedTheme);
      setTheme(nextTheme);
      dispatch(setAppTheme(resolvedTheme));
    }
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error('useTheme must be used within a ThemeProvider');

  return context;
};
